import { z } from "zod"

import { entityIdSchema, tenantIdSchema } from "./identifiers.js"
import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/)
const safeVariablesSchema = z.record(
  z.string().regex(/^[a-z][a-zA-Z0-9]{0,31}$/),
  z.string().max(256),
).refine((value) => Object.keys(value).length <= 20)

export const platformCommunicationProviderV1Schema = z.enum([
  "email",
  "quo",
  "twilio",
])

export const platformCommunicationRouteV1Schema = z.object({
  routeId: entityIdSchema,
  namespace: z.literal("platform"),
  channel: z.enum(["email", "sms"]),
  providerCode: platformCommunicationProviderV1Schema,
  senderIdentityReference: z.string().regex(/^secretref:\/\/[a-z0-9][a-z0-9/_.-]{2,191}$/),
  enabled: z.boolean(),
}).strict().superRefine((route, context) => {
  if (
    (route.channel === "email" && route.providerCode !== "email")
    || (route.channel === "sms" && route.providerCode === "email")
  ) {
    context.addIssue({
      code: "custom",
      message: "Platform route channel and provider do not agree.",
    })
  }
})

export const platformCommunicationPreferenceV1Schema = z.object({
  preferenceId: entityIdSchema,
  recipientReference: z.string().regex(/^platform-recipient:[0-9a-f-]{36}$/),
  channel: z.enum(["email", "sms"]),
  state: z.enum(["enabled", "suppressed"]),
  reasonCode: codeSchema.nullable(),
  version: z.number().int().positive(),
  updatedAt: timestampSchema,
}).strict()

export const platformCommunicationStateV1Schema = z.enum([
  "queued",
  "sending",
  "sent",
  "delivered",
  "failed",
  "uncertain",
  "suppressed",
  "manual_fallback_required",
])

const communicationInputShape = {
  contractVersion: z.literal(1),
  communicationId: entityIdSchema,
  logicalMessageId: entityIdSchema,
  routeId: entityIdSchema,
  recipientReference: z.string().regex(/^platform-recipient:[0-9a-f-]{36}$/),
  templateCode: codeSchema,
  variables: safeVariablesSchema,
  relatedEntityType: z.enum([
    "platform_support_case",
    "platform_incident",
    "security_review",
    "support_session",
  ]),
  relatedEntityId: entityIdSchema,
  targetTenantId: tenantIdSchema.nullable(),
  priority: z.enum(["routine", "urgent", "critical"]),
  escalationDueAt: timestampSchema.nullable(),
  deduplicationKey: z.string().regex(/^platform:[A-Za-z0-9._:-]{8,119}$/),
} as const

export const platformCommunicationV1Schema = z.object({
  ...communicationInputShape,
  providerCode: platformCommunicationProviderV1Schema,
  state: platformCommunicationStateV1Schema,
  providerMessageReference: z.string().max(128).nullable(),
  attemptCount: z.number().int().nonnegative().max(20),
  manualFallbackFromCommunicationId: entityIdSchema.nullable(),
  version: z.number().int().positive(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
}).strict()

const enqueueSchema = z.object({
  action: z.literal("enqueue"),
  ...communicationInputShape,
}).strict()

const deliverySchema = z.object({
  action: z.literal("record_delivery"),
  communicationId: entityIdSchema,
  providerCode: platformCommunicationProviderV1Schema,
  providerEventReference: z.string().min(8).max(128),
  state: z.enum(["sent", "delivered", "failed", "uncertain"]),
  occurredAt: timestampSchema,
}).strict()

const escalateSchema = z.object({
  action: z.literal("escalate"),
  communicationId: entityIdSchema,
  reasonCode: z.enum(["delivery_failed", "delivery_uncertain", "sla_breached"]),
}).strict()

const reconcileSchema = z.object({
  action: z.literal("reconcile"),
  communicationId: entityIdSchema,
  providerCode: platformCommunicationProviderV1Schema,
  providerMessageReference: z.string().min(8).max(128),
  reconciledState: z.enum(["sent", "delivered", "failed", "uncertain"]),
  observedAt: timestampSchema,
}).strict()

const manualFallbackSchema = z.object({
  action: z.literal("confirm_manual_fallback"),
  communicationId: entityIdSchema,
  fallbackCommunicationId: entityIdSchema,
  targetRouteId: entityIdSchema,
  targetProviderCode: z.enum(["quo", "twilio"]),
  reasonCode: z.enum(["uncertain_delivery", "provider_exhausted"]),
  duplicateRiskAcknowledged: z.literal(true),
}).strict().refine(
  (value) => value.communicationId !== value.fallbackCommunicationId,
  { message: "Fallback must create a distinct delivery." },
)

export const managePlatformCommunicationV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema,
  request: z.discriminatedUnion("action", [
    enqueueSchema,
    deliverySchema,
    escalateSchema,
    reconcileSchema,
    manualFallbackSchema,
  ]),
}).strict().superRefine((command, context) => {
  if (
    command.envelope.target.scope !== "platform"
    || command.envelope.target.targetType !== "platform_communication"
    || command.envelope.target.targetId !== command.request.communicationId
  ) {
    context.addIssue({
      code: "custom",
      message: "Platform communications use only the Platform command context.",
    })
  }
  if (
    command.request.action === "confirm_manual_fallback"
    && command.envelope.requiredApprovalIds.length !== 1
  ) {
    context.addIssue({
      code: "custom",
      message: "Cross-provider fallback requires one current approval.",
    })
  }
})

export const platformCommunicationDeliveryResultV1Schema = z.object({
  providerCode: platformCommunicationProviderV1Schema,
  providerMessageReference: z.string().min(8).max(128),
  state: z.enum(["sent", "failed", "uncertain"]),
  safeFailureCode: codeSchema.nullable(),
}).strict()

export type PlatformCommunicationRouteV1 = z.infer<
  typeof platformCommunicationRouteV1Schema
>
export type PlatformCommunicationV1 = z.infer<typeof platformCommunicationV1Schema>
export type ManagePlatformCommunicationV1 = z.infer<
  typeof managePlatformCommunicationV1Schema
>
export type PlatformCommunicationDeliveryResultV1 = z.infer<
  typeof platformCommunicationDeliveryResultV1Schema
>
